import { NgModule } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';

// Componentes
import { ActionButtonComponent } from './components/action-button/action-button.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { FormFieldLockIconComponent } from './components/form-field-lock-icon/form-field-lock-icon.component';
import { GoogleActionButtonComponent } from './components/google-action-button/google-action-button.component';
import { AlertComponent } from './components/alert/alert.component';
import { DividerTextComponent } from './components/divider-text/divider-text.component';
import { CornerButtonComponent } from './components/corner-close-button/corner-button.component';
import { QuestionAndAnswerComponent } from './components/question-and-answer/question-and-answer.component';
import { ProductCardComponent } from './components/product-card/product-card.component';
import { ImageInAspectRatioComponent } from './components/image-in-aspect-ratio/image-in-aspect-ratio.component';
import { ContainedProgressBarComponent } from './components/contained-progress-bar/contained-progress-bar.component';
import { GenericConfirmationDialogComponent } from './components/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { ModalTitleComponent } from './components/modal-title/modal-title.component';
import { NoDataRowComponent } from './components/no-data-row/no-data-row.component';
import { QuestionsFilterFormComponent } from './components/questions-filter-form/questions-filter-form.component';
import { NoElementsTemplateComponent } from './components/no-elements-template/no-elements-template.component';
import { SingleInputSearchFieldComponent } from './components/single-input-search-field/single-input-search-field.component';
import { StarRatingComponent } from './components/star-rating/star-rating.component';
import { SocialMediaShareButtonsComponent } from './components/social-media-share-buttons/social-media-share-buttons.component';
import { ProductSearchComponent } from './components/product-search/product-search.component';
import { ProductSearchFilterFormComponent } from './components/product-search-filter-form/product-search-filter-form.component';
import { BackToTopButtonComponent } from './components/back-to-top-button/back-to-top-button.component';
import { AddressFormComponent } from './components/address-form/address-form.component';
import { AsyncSimplePaginatorComponent } from './components/async-simple-paginator/async-simple-paginator.component';
import { AsyncMatPaginatorComponent } from './components/async-mat-paginator/async-mat-paginator.component';
import { ImageDialogComponent } from './components/image-dialog/image-dialog.component';
import { ImageCropperDialogComponent } from './components/image-cropper-dialog/image-cropper-dialog.component';

// Directivas
import { AtScreenResolutionDirective } from './directives/at-screen-resolution.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { SmoothHeightAnimationDirective } from './directives/smooth-height-animation.directive';
import { NumericInputDirective } from './directives/numeric-input.directive';

// Pipés
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { SafeCssPipe } from './pipes/safe-css.pipe';
import { ProperEnumPipe } from './pipes/proper-enum.pipe';
import { SaleStatusPipe } from './pipes/sale-status.pipe';
import { TimeAgoPipe } from './pipes/time-ago.pipe';
import { MonthPipe } from './pipes/month.pipe';
import { ProductStatusPipe } from './pipes/product-status.pipe';
import { ProductVariantsPipe } from './pipes/product-variants.pipe';
import { ProperCurrencyPipe } from './pipes/proper-currency.pipe';
import { SafeResourceUrlPipe } from './pipes/safe-resource-url.pipe';
import { DayOfTheWeekPipe } from './pipes/day-of-the-week.pipe';
import { PurchaseStatusPipe } from './pipes/purchase-status.pipe';

// Material
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { ImageCropperModule } from 'ngx-image-cropper';

@NgModule({
	declarations: [
		ActionButtonComponent,
		FormFieldLockIconComponent,
		AvatarComponent,
		DividerTextComponent,
		GoogleActionButtonComponent,
		AlertComponent,
		CornerButtonComponent,
		ImageInAspectRatioComponent,
		QuestionAndAnswerComponent,
		ProductCardComponent,
		ModalTitleComponent,
		GenericConfirmationDialogComponent,
		ContainedProgressBarComponent,
		NoDataRowComponent,
		AsyncMatPaginatorComponent,
		QuestionsFilterFormComponent,
		SingleInputSearchFieldComponent,
		NoElementsTemplateComponent,
		StarRatingComponent,
		SocialMediaShareButtonsComponent,
		ProductSearchComponent,
		ProductSearchFilterFormComponent,
		BackToTopButtonComponent,
		AddressFormComponent,
		AsyncSimplePaginatorComponent,
		ImageDialogComponent,
		ImageCropperDialogComponent,

		AtScreenResolutionDirective,
		AutofocusDirective,
		SmoothHeightAnimationDirective,
		NumericInputDirective,

		ProductStatusPipe,
		ProductVariantsPipe,
		SaleStatusPipe,
		SafeCssPipe,
		SafeHtmlPipe,
		ProperEnumPipe,
		TimeAgoPipe,
		MonthPipe,
		ProperCurrencyPipe,
		SafeResourceUrlPipe,
		DayOfTheWeekPipe,
		PurchaseStatusPipe,
	],
	imports: [
		CommonModule,
		RouterModule,
		ReactiveFormsModule,

		MatProgressSpinnerModule,
		MatButtonModule,
		MatIconModule,
		MatCardModule,
		MatDialogModule,
		MatProgressBarModule,
		MatTooltipModule,
		MatPaginatorModule,
		MatInputModule,
		MatSelectModule,
		MatIconModule,
		MatChipsModule,
		MatTabsModule,
		ImageCropperModule
	],
	exports: [
		ActionButtonComponent,
		FormFieldLockIconComponent,
		AvatarComponent,
		DividerTextComponent,
		GoogleActionButtonComponent,
		AlertComponent,
		CornerButtonComponent,
		ImageInAspectRatioComponent,
		QuestionAndAnswerComponent,
		ProductCardComponent,
		ModalTitleComponent,
		ContainedProgressBarComponent,
		NoDataRowComponent,
		AsyncMatPaginatorComponent,
		QuestionsFilterFormComponent,
		SingleInputSearchFieldComponent,
		NoElementsTemplateComponent,
		StarRatingComponent,
		SocialMediaShareButtonsComponent,
		ProductSearchComponent,
		ProductSearchFilterFormComponent,
		BackToTopButtonComponent,
        AddressFormComponent,
		AsyncSimplePaginatorComponent,

		AtScreenResolutionDirective,
		AutofocusDirective,
		SmoothHeightAnimationDirective,
		NumericInputDirective,

		ProductStatusPipe,
		ProductVariantsPipe,
		SaleStatusPipe,
		SafeCssPipe,
		SafeHtmlPipe,
		ProperEnumPipe,
		TimeAgoPipe,
		MonthPipe,
		ProperCurrencyPipe,
		SafeResourceUrlPipe,
		DayOfTheWeekPipe,
		PurchaseStatusPipe
	],
	providers: [
		DecimalPipe
	]
})
export class SharedModule { }
